import * as React from "react";
import {inject, observer} from "mobx-react";
import {observable} from "mobx";
import {TabView} from "../../../../widgets/tab/TabView";
import {SettingTableTab} from "../abstract/SettingTableTab";
import {TabChild} from "../../../../widgets/tab/TabChild";
import type {Field} from "../../../../../data/models/field/Field";
import {XyiconFeature} from "../../../../../generated/api/base";
import type {AppState} from "../../../../../data/state/AppState";
import {featureTitles} from "../../../../../data/state/AppStateConstants";
import {FieldDataTypes} from "../../../../../data/models/field/FieldDataTypes";
import type {IHeader} from "../../../../widgets/table/SimpleTable";
import {CreateFieldPanel} from "./CreateFieldPanel";
import {FieldDetailsContent} from "./FieldDetailsContent";

interface IFieldSettingsProps {
	readonly feature: XyiconFeature;
	readonly typesFeature: XyiconFeature;
	readonly appState?: AppState;
}

interface IFieldSettingsState {
	headers: IHeader[];
}

@inject("appState")
@observer
export class FieldSettings extends React.Component<IFieldSettingsProps, IFieldSettingsState> {
	@observable
	private _headers: IHeader[] = [];

	@observable
	private _appState: AppState;

	constructor(props: IFieldSettingsProps) {
		super(props);
		this._appState = props.appState;

		this._headers = [
			{
				id: "refId",
				title: "ID",
			},
			{
				id: "name",
				title: "Name",
			},
			{
				id: "type",
				title: "Type",
			},
			{
				id: "displayOnLinks",
				title: "Display On Links",
			},
			[XyiconFeature.Boundary, XyiconFeature.Xyicon].includes(props.feature) && {
				id: "hasFormula",
				title: "Has Formula",
			},
			props.appState.actions.isFieldValidationRuleAvailableInOrganization(props.feature) && {
				id: "validation",
				title: "Validation Rule",
			},
			{
				id: "helperText",
				title: "Helper Text",
			},
			{
				id: "description",
				title: "Field Description",
			},
		].filter((i) => !!i);
	}

	private getFields = (item: Field, index: number) => {
		return this._headers.map((header) => {
			// TODO
			const id = header.id;

			switch (id) {
				case "displayOnLinks":
					return this._appState.actions.getBooleanFieldTitle(item.displayOnLinks);
				case "hasFormula":
					return this._appState.actions.getBooleanFieldTitle(item.hasFormula);
				case "type":
					const dataType = FieldDataTypes.map[item.dataType];

					if (dataType) {
						return dataType.title;
					}
					break;
				case "validation":
					return item.validation;
				case "description":
					return item.fieldDescription;
				default:
					return item[id as "id" | "name"];
			}
		});
	};

	public override componentDidUpdate(prevProps: IFieldSettingsProps) {
		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}
	}

	public override render() {
		const {feature, typesFeature} = this.props;

		return (
			<SettingTableTab
				headers={this._headers}
				getFields={this.getFields}
				feature={feature}
				typesFeature={typesFeature}
				kind="fields"
				create={(onCancel, onCreated) => (
					<CreateFieldPanel
						feature={feature}
						typesFeature={typesFeature}
						onCancel={onCancel}
						onCreated={onCreated}
					/>
				)}
				right={(selected) => (
					<TabView
						className="SidePanel"
						selectedTabId="details"
					>
						<TabChild
							id="details"
							icon="details"
							title="Details"
							label="Details"
						>
							<FieldDetailsContent
								selected={selected}
								typesFeature={typesFeature}
							/>
						</TabChild>
					</TabView>
				)}
				emptyListText={`There are no ${featureTitles[feature]} Fields to list.`}
			/>
		);
	}
}
